<script lang="ts">
	import { getRentalTemplateContext } from "./rental-template-context.svelte";
	import { fade } from "svelte/transition";
	import { quintOut } from "svelte/easing";
	import ExpandIcon from "@lucide/svelte/icons/expand";

	const templateContext = getRentalTemplateContext()!;

	let enlargedImageUrl: string | null = $state(null);
	let enlargedImageAlt: string | null = $state(null);

	function enlargeImage(url: string, alt: string) {
		enlargedImageUrl = url;
		enlargedImageAlt = alt;
		setTimeout(() => {
			const modalElement = document.getElementById("image-modal");
			modalElement?.focus();
		}, 0);
	}

	function closeEnlargedImage() {
		enlargedImageUrl = null;
		enlargedImageAlt = null;
	}

	// Function to handle keydown events, specifically for the Escape key
	function handleKeydown(event: KeyboardEvent) {
		if (event.key === "Escape" && enlargedImageUrl) {
			closeEnlargedImage();
		}
	}
</script>

<svelte:window onkeydown={handleKeydown} />

<section id="floor-plans" class="bg-white py-16">
	<div class="container mx-auto px-4">
		<h2 class="mb-6 text-center text-4xl font-medium text-slate-700">FLOOR PLANS</h2>
		<hr class="border-primary mx-auto mb-4 w-[100px] border-2" />
		<p class="mb-16 text-center">Click on a floor plan to enlarge</p>

		<div class="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
			{#each templateContext.floorPlans as plan (plan.name)}
				<div class="group flex flex-col items-center">
					<button
						type="button"
						class="hover:border-primary focus-visible:ring-primary relative mb-4 flex h-64 w-full cursor-pointer items-center justify-center overflow-hidden rounded border border-gray-200 bg-white p-4 text-inherit shadow-md transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-2xl focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2"
						onclick={() => enlargeImage(plan.image, plan.name)}
						onkeydown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								enlargeImage(plan.image, plan.name);
							}
						}}
						aria-haspopup="dialog"
						aria-label={`Enlarge floor plan: ${plan.name}`}
					>
						<img
							src={plan.image}
							alt={plan.name}
							class="block max-h-full max-w-full object-contain transition-transform duration-500 ease-in-out group-hover:scale-110"
							width="600"
							height="600"
							loading="lazy"
						/>
						<!-- Expand icon overlay -->
						<div
							class="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
						>
							<ExpandIcon class="h-12 w-12 text-white drop-shadow-lg" />
						</div>
					</button>

					<div class="text-center">
						<h3
							class="group-hover:text-primary text-lg font-medium uppercase transition-colors duration-300"
						>
							{plan.name}
						</h3>
						<p>
							{plan.beds} bedroom{plan.beds > 1 ? "s" : ""} | {plan.baths}
							bathroom{plan.baths > 1 ? "s" : ""}
						</p>
						<p>
							{plan.sqFt.min === plan.sqFt.max
								? plan.sqFt.min
								: `${plan.sqFt.min}-${plan.sqFt.max}`} Sq Ft
						</p>
					</div>
				</div>
			{/each}
		</div>
	</div>
</section>

{#if enlargedImageUrl}
	<div
		transition:fade={{ duration: 150, easing: quintOut }}
		id="image-modal"
		role="dialog"
		aria-modal="true"
		aria-labelledby="modal-title"
		tabindex="-1"
		class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
		onclick={closeEnlargedImage}
		onkeydown={(e) => {
			if (e.key === "Escape") {
				closeEnlargedImage();
			}
		}}
	>
		<div class="relative max-h-[90vh] max-w-[90vw] rounded bg-white p-4 shadow-xl">
			<h3 id="modal-title" class="sr-only">Enlarged Floor Plan: {enlargedImageAlt}</h3>

			<img
				src={enlargedImageUrl}
				alt={enlargedImageAlt ?? "Enlarged floor plan"}
				class="block max-h-[calc(90vh-4rem)] w-auto max-w-full object-contain"
				width="600"
				height="600"
			/>

			<button
				type="button"
				aria-label="Close enlarged image view"
				class="absolute -top-3 -right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-white transition hover:bg-slate-900 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-700"
				onclick={closeEnlargedImage}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					stroke-width="2"
					stroke="currentColor"
					class="h-5 w-5"
				>
					<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
				</svg>
			</button>
		</div>
	</div>
{/if}
